import { useQueryClient } from '@tanstack/react-query';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { AsyncSelect, AsyncSelectProps, OptionType } from '@components/Select';
import { primeEhrListByIdsCache } from '@patient/queries/ehrQueries';

import { CREATE_PATIENT_VALUE, LabelFormat } from './constants';
import { usePatientOptions } from './hooks/usePatientOptions';

interface Props extends Omit<AsyncSelectProps, 'loadOptions' | 'selectedValue'> {
    patientId?: string | null;
    executeId?: string;
    availableIds?: string[];
    filteredPatientIds?: string[];
    fullInfo?: boolean;
    labelFormat?: LabelFormat[];
    fromLastViewedPatients?: boolean;
    defaultPatientIds?: string[];
    additionalOptions?: OptionType<any, { isEhrBlocked?: boolean }>[];
    onCreatePatientClick?(): void;
}

export function PatientSelect({
    patientId,
    executeId,
    availableIds,
    filteredPatientIds,
    fullInfo,
    labelFormat,
    fromLastViewedPatients,
    defaultPatientIds,
    additionalOptions,
    onCreatePatientClick,
    onChange,
    ...props
}: Props) {
    const queryClient = useQueryClient();
    const { defaultOptions, value, selectedValue, loadOptions } = usePatientOptions({
        patientId,
        executeId,
        availableIds,
        filteredPatientIds,
        labelFormat,
        fromLastViewedPatients,
        defaultPatientIds,
        additionalOptions,
        onCreatePatientClick,
    });

    const handleChange = (option: Nullable<OptionType>, name?: string): void => {
        if (onCreatePatientClick && option?.value === CREATE_PATIENT_VALUE) {
            onCreatePatientClick();
            return;
        }

        onChange(option, name);

        if (typeof option?.value === 'string' && option.value !== '') {
            primeEhrListByIdsCache(queryClient, [option.value]);
        }
    };

    return (
        <AsyncSelect
            value={value}
            defaultOptions={defaultOptions}
            selectedValue={selectedValue}
            loadOptions={loadOptions}
            onChange={handleChange}
            {...props}
        />
    );
}
